
//this is mock up until the mr with the fw/hw branch
export interface MockFirmware {
    id: number;
    name: string;
}

export interface CannyDevice {
    id: number,
    created_at: string,
    firmware_version: FirmwareBase | null,
    firmware_base_id: number | null,
    hardware_version: string,
    mac_address: string | null,
    pin: number | null,
    qc_pass: boolean,
    serial_no: string,
    status: string,
    allow_record: boolean,
    notification_tokens: [string] | null,
    latest_firmware_id: number | null,
}

export interface LatestFirmwareGlobal {
    id: number,
    firmware_base_id: number | null,
}

export interface RecordedVehicles {
    id: number,
    created_at: string,
    vehicle_name: string,
    vehicle_model: string,
    vehicle_year: string,
    type: string,
    cannydevice: string,
    recorded_file_list: any,
    canny_device_serial_no: string,
    buad_rate_speed: string,
    comment: string
}

export interface SmartButtons {
    id: number,
    button_sn: string,
    button_mac: string,
    connected_canny: string | null,
    paired: boolean
}

export enum MobileNotificationType {
    FIRMWARE_UPDATE = 'FIRMWARE_UPDATE',
    NEW_CONFIG_AVAILABLE = 'NEW_CONFIG_AVAILABLE'
}

export enum FirebaseAppVariant {
    production = 'prod',
    developement = 'dev'
}

export interface MobileNotification {
    id: number,
    created_at: Date,
    sent_at: Date,
    title: string,
    body: string,
    type: MobileNotificationType,
    sent_status: boolean
    notes: string
}
export type MobileNotificationIndex = keyof MobileNotification;

export type CannyDeviceIndex = keyof CannyDevice;
export type SmartButtonsIndex = keyof SmartButtons;

export interface CannyIForm {
    hardware_version: string,
    // firmware_version: MockFirmware | undefined
    items_in_batch: number | null,


}
export interface StringSelectObject {
    value: string | boolean,
    label: string
}

// Static reference info only - roles are no longer DB-backed, they come
// live from Azure AD (see app.py's is_admin_from_session). GET /roles
// returns this shape purely for the read-only Role Management page.
export interface Role {
    role_name: string;
    description: string;
}

export interface ActionCategory {
    id: number;
    key: string;
    name: string;
}

export interface Action {
    id: number;
    key: string;
    name: string;
    action_category_id: number;
    action_category: ActionCategory;
}

export interface CurrentUser {
    name: string;
    allowedActions: string[];
    roleName: string;
}

export interface Endpoint {
    endpoint: string;
    location: string;
    methods: string;
}

export interface User {
    id: number;
    user_email: string;
    is_service_account: boolean;
    allowed_endpoints: string[] | null;
    // Mirrors the Azure AD "Administrator" app role, synced into the DB on
    // each authenticated request (see app.py's load_user) - always false
    // for service accounts, which never sign in through Azure.
    is_admin: boolean;
}

export type UserIndex = keyof User;

export interface VehicleJsonSchemaDesc {
    schema_id: number;
    TEST: {
        config_id: number;
        valid: boolean
    };
    RELEASE: {
        config_id: number;
        valid: boolean
    };
}

export interface VehicleJsonSchemaRefItem extends Record<string, VehicleJsonSchemaDesc> { }

export enum MobileNotificationStatusType {
    NO_NOTIFICATION = 'NO_NOTIFICATION',
    SENT = 'SENT',
    PENDING = 'PENDING'
}

export interface VehicleItem {
    id: number,
    created_at: Date | null,
    date_released: Date | null,
    manufacturer: string,
    model: string,
    type: string | null,
    year_start: string,
    year_end: string,
    status: string | null,
    wiring_instruction: string | null,
    json_config: JSON | {},
    json_config_versions: VehicleJsonSchemaRefItem | null,
    notification_status: MobileNotificationStatusType
}

export type VehicleItemIndex = keyof VehicleItem;

export interface JsonSchemaItem {
    id: number,
    created_at: Date | null,
    version_schema_name: string,
    json_schema: JSON | {},
}

export interface LinBusConfig {
    baudrate_bps: '2400' | '9600' | '10417' | '19200' | '';
    lin_version: 'v1_3' | 'v2_0' | 'v2_1' | 'v2_2A' | '';
}

export interface LinFrameConfig {
    data_len: number | null;
    checksum_type: 'classic' | 'enhanced' | '';
    timeout_ms: number | null;
}

export type LinMessageConfig = Record<string, LinFrameConfig>;

// A CONF 3.0 rule is either a CAN mask/range check or a LIN signal-
// comparison tree, discriminated by `type` - both live in the same
// `rules[]` array.
export interface CanRule {
    type: 'CAN';
    description: string;
    message_id: string;
    mask: string[];
    range_low: string[];
    range_high: string[];
}

// A named bit-range within a LIN frame (see lin_signals[]) - a pure
// definition, no comparison value. Referenced by LinSignalCompare.signal.
export interface LinSignal {
    name: string;
    frame_id: string;
    bit_number: number;
    bit_length: number;
}

export type LinCompareOperator = 'eq' | 'gt' | 'lt' | 'gte' | 'lte' | 'ne';

export interface LinSignalCompare {
    signal: number;
    operator: LinCompareOperator;
    value: number;
}

// Recursive and/or/not tree, same shape as functionality_control's
// condition tree, except leaves compare a named LIN signal against a value
// instead of referencing another rule.
export type LinCondition =
    | { signal_compare: LinSignalCompare }
    | { and: LinCondition[] }
    | { or: LinCondition[] }
    | { not: [LinCondition] };

export interface LinRule {
    type: 'LIN';
    description: string;
    condition: LinCondition;
}

export type Rule = CanRule | LinRule;

export type FunctionalityCondition =
    | { rule: number }
    | { and: FunctionalityCondition[] }
    | { or: FunctionalityCondition[] }
    | { not: [FunctionalityCondition] };

export interface VehicleJsonConfig {
    id: number;
    created_at: Date | null;
    json_config: JSON;
    status: string | null;
    json_schema_base: JsonSchemaItem;
    json_schema_base_id: number;
    vehicle_id: number;
}


export interface FirmwareBase {
    id: number,
    created_at: Date | null,
    version_name: string,
    commit_hash: string,
    changelog: string,
    mandatory: boolean | null,
    binary_loader: FileList,
    binary_app: FileList,
    binary_app_gbl: FileList,
    status: string,
    json_schema_base_id: number,
    json_schema_base: JsonSchemaItem,
    notification_status: MobileNotificationStatusType
}

export interface Comment {
    id: number,
    target_id: number,
    comment: string,
    formatted_comment: string,
    target: string,
    user_name: string
    user_id: number,
    created_at: Date | null,
}



export type JsonSchemaListResponse = {
    success: boolean,
    content: JsonSchemaItem[]
}

